'use client';

import React from 'react';
import { IconProps } from './types';
import { iconMappings } from './iconMappings';
import { useIconProvider } from './IconProvider';

export function Icon({
  name,
  size,
  color,
  className,
  fallback,
  'aria-label': ariaLabel,
  onClick,
  title,
}: IconProps) {
  const { config } = useIconProvider();

  const actualSize = size || config.defaultSize;
  const actualColor = color || config.defaultColor;

  const pixelSize = config.sizeMap[actualSize];
  const colorValue = config.colorMap[actualColor];

  // Get the icon component, with fallback support
  let IconComponent = iconMappings[name];

  if (!IconComponent && fallback) {
    IconComponent = iconMappings[fallback];
  }

  if (!IconComponent) {
    console.warn(
      `Icon "${name}" not found in current icon set. ${fallback ? `Fallback "${fallback}" also not found.` : 'No fallback provided.'}`,
    );
    // Return a simple placeholder if icon is missing
    return (
      <div
        className={className}
        style={{
          width: pixelSize,
          height: pixelSize,
          backgroundColor:
            colorValue === 'currentColor' ? 'currentColor' : colorValue,
          borderRadius: '2px',
          opacity: 0.3,
        }}
        aria-label={ariaLabel || `Missing icon: ${name}`}
        title={title || `Missing icon: ${name}`}
        onClick={onClick}
      />
    );
  }

  return (
    <IconComponent
      size={pixelSize}
      color={colorValue}
      className={className}
      aria-label={ariaLabel || name}
      title={title}
      onClick={onClick}
    />
  );
}

// Convenience function for common icon usage patterns
export function createIconComponent(iconName: IconProps['name']) {
  return function IconWrapper(props: Omit<IconProps, 'name'>) {
    return <Icon name={iconName} {...props} />;
  };
}
